/**
 * Monthly Review — is each month ready for QuickBooks to close? Phase 5, FINANCE.md §7.
 *
 * One card per month from `GET /api/finance/monthly-review`: the open items `monthlyReview`
 * counted from the same views the Reconciliation and Bookkeeping screens read, each with the
 * screen that resolves it, and a verdict that is READY exactly when nothing is open. The screen
 * adds nothing up and cannot mark a month clean: there is no button here, by design (ADR-0007) —
 * a month becomes ready by resolving its items where they live.
 */

import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api, type FinanceNarrationResponse, type MonthlyReviewResponse } from '../../api.js';
import { EntityPicker, useEntities, useEntityId } from '../../components/EntityPicker.js';
import { NarrationCard } from '../../components/NarrationCard.js';
import { formatMoney } from '../../money.js';

const MONTHS = 6;

const SCREEN_PATH: Record<string, string> = {
  bookkeeping: '/finance/bookkeeping',
  transactions: '/finance/transactions',
  reconciliation: '/finance/reconciliation',
  chart: '/finance/chart',
};

export function MonthlyReviewPage(): ReactNode {
  const entityId = useEntityId();
  const entities = useEntities();

  const review = useQuery({
    queryKey: ['finance', 'monthly-review', entityId, MONTHS],
    enabled: entityId !== null,
    queryFn: () =>
      api.get<MonthlyReviewResponse>(
        `/api/finance/monthly-review?entity=${entityId}&months=${MONTHS}`,
      ),
  });

  // What the Finance Agent said about these months and this entity's cash, if it has run.
  const narration = useQuery({
    queryKey: ['finance', 'narration', entityId],
    enabled: entityId !== null,
    queryFn: () => api.get<FinanceNarrationResponse>(`/api/finance/narration?entity=${entityId}`),
  });
  const said = narration.data?.narration;
  const items = said?.items ?? [];

  const noEntities = entities.data !== undefined && entities.data.entities.length === 0;
  const data = review.data;

  return (
    <div className="page">
      <header className="page-head">
        <div>
          <h1>Monthly Review</h1>
          <p className="subtitle">
            QuickBooks closes the month; this says whether it is ready. A month is ready exactly
            when nothing below is open — and nothing here can make it so.
          </p>
        </div>
      </header>

      <div className="filters">
        <EntityPicker />
      </div>

      {(review.isLoading || (entityId === null && entities.isLoading)) && (
        <p className="muted">Loading…</p>
      )}
      {review.isError && <p className="error">Could not load the review: {review.error.message}</p>}
      {noEntities && (
        <div className="card empty">
          <h2>No entities.</h2>
          <p>
            Run <code>pnpm seed</code> to load the fictional sample portfolio.
          </p>
        </div>
      )}

      {data && data.months.length === 0 && (
        <div className="card empty">
          <h2>No months to review.</h2>
          <p>
            Nothing in the mirror for this entity yet. Lines arrive from the QuickBooks connector —{' '}
            <code>pnpm qbo:sync</code>.
          </p>
        </div>
      )}

      {data?.months.map((m) => (
        <section key={m.month} className="card recon-month">
          <header className="recon-month-head">
            <h2>{m.month.slice(0, 7)}</h2>
            {m.ready ? (
              <span className="status status-approved">Ready to close</span>
            ) : (
              <span className="status status-conflict">
                {m.openCount} open {m.openCount === 1 ? 'item' : 'items'}
              </span>
            )}
          </header>
          {m.ready ? (
            <p className="muted">
              Every line classified and on the chart, no duplicates, transfers and payouts matched,
              no anomaly waiting, nothing moved after approval.
            </p>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Open item</th>
                  <th className="num">Count</th>
                  <th>Resolve on</th>
                </tr>
              </thead>
              <tbody>
                {m.open.map((item) => (
                  <tr key={item.key}>
                    <td>
                      {item.label}
                      {item.key === 'unmapped_lines' && m.unmapped_cents !== '0' && (
                        <span className="muted small"> · {formatMoney(m.unmapped_cents)}</span>
                      )}
                    </td>
                    <td className="num">{item.count}</td>
                    <td>
                      <Link to={SCREEN_PATH[item.screen] ?? '/finance/bookkeeping'}>
                        {item.screen === 'chart' ? 'Settings (chart mapping)' : item.screen}
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {items
            .filter((i) => i.kind === 'close_readiness' && i.month === m.month.slice(0, 7))
            .map((i) => (
              <article key={i.title} className="narration-inline">
                <p>
                  <strong>The Finance Agent:</strong> {i.narrative}
                </p>
                <ul className="small">
                  {i.why.map((why) => (
                    <li key={why}>{why}</li>
                  ))}
                </ul>
                <p className="muted small">
                  AI narration · run {said?.run_id.slice(0, 8)} · sources{' '}
                  {i.data_sources.join(', ')}
                </p>
              </article>
            ))}
        </section>
      ))}

      {data && data.months.length > 0 && (
        <NarrationCard
          title="Cash, in the Finance Agent’s words"
          narration={narration.data?.narration}
          items={items.filter((i) => i.kind === 'cash')}
          quiet="The last narration said nothing about this entity’s cash: no cash accounts in the mirror, or nothing to say."
        />
      )}

      {data && (
        <p className="muted small recon-as-of">
          Last {MONTHS} months on the view · as of {new Date(data.as_of).toLocaleString()} · no
          lock, no exception: the counts are the Reconciliation and Bookkeeping screens&apos; own.
        </p>
      )}
    </div>
  );
}
